/**
 * AI CONTEXT
 * Purpose: the artwork on a StandoutMomentCard — scans from that moment's
 *   class session only (see `selectMomentArtwork`). The newest piece shows
 *   immediately; the rest stay behind See more, under the scroller.
 *   Share and don't-share on the thumb are the photo's own writes
 *   (isShared / dontShare), separate from the message. Don't share never
 *   sets isHidden, so the photo stays here; the same thumb button turns it
 *   back off. Each thumb is the shared
 *   editable thumbnail: zoom, crop, and rotate save onto that student's
 *   artwork, and the subscription updates every other surface.
 * Data: which pieces belong to the moment comes from the card's student
 *   join. The thumb subscribes for the image itself. Renders nothing when
 *   the session has no matching artwork.
 * Used by: StandoutMomentCard.
 */
import { App, Button } from "antd"
import { useCallback, useMemo, useState } from "react"

import Firebase from "#classes/Firebase"
import ArtworkEditableThumb, {
  ARTWORK_EDIT_SOURCE,
} from "#components/ArtworkTiles/ArtworkEditableThumb"
import {
  buildArtworkDontShareUpdate,
  buildArtworkSharedUpdate,
  isArtworkDontShare,
  isClassImageShared,
} from "#components/forms/AddStudent/studentArtworkTilesHelpers"
import { useUserContext } from "#contexts/UserContextProvider"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { type StandoutMomentConstructor } from "#customTypes/composedTypes/standoutTypes"
import {
  type StudentArtwork,
  type StudentConstructor,
} from "#customTypes/composedTypes/studentTypes"
import useHasPermission from "#hooks/newHooks/useHasPermission"

import DontShareReasonModal from "./DontShareReasonModal"
import { useFamilyDelivery } from "./familyDeliveryContext"
import { selectMomentArtwork } from "./standoutReviewCardHelpers"
import { itemsSharedMessage } from "./standoutSessionGroups"
import { useKeyedSingleFlight } from "./useSingleFlight"

const TILE_SIZE = 88

const shareKey = (artworkId: string) => `share:${artworkId}`
const dontShareKey = (artworkId: string) => `dontShare:${artworkId}`

interface MomentArtworkStripProps {
  studentId: string
  student: Pick<StudentConstructor, "artwork"> | undefined
  moment: Pick<StandoutMomentConstructor, "classSessionId">
}

const MomentArtworkStrip = ({
  studentId,
  student,
  moment,
}: MomentArtworkStripProps) => {
  const { message } = App.useApp()
  const { currentUserId } = useUserContext()
  const delivery = useFamilyDelivery()
  const canShare = useHasPermission(
    RESOURCES.STANDOUT_REVIEW_PAGE,
    USER_ACTIONS.UPDATE
  )
  const [expanded, setExpanded] = useState(false)
  const { pendingKeys, run: runPiece } = useKeyedSingleFlight()
  const [reasonFor, setReasonFor] = useState<StudentArtwork | null>(null)

  const allArtwork = useMemo(
    () => selectMomentArtwork(student, moment),
    [student, moment]
  )
  const visible = expanded ? allArtwork : allArtwork.slice(0, 1)
  const hiddenCount = Math.max(allArtwork.length - 1, 0)

  const toggleExpanded = useCallback(() => {
    setExpanded((currentlyExpanded) => !currentlyExpanded)
  }, [])

  const sharePiece = useCallback(
    (piece: StudentArtwork) =>
      runPiece(shareKey(piece.id), async () => {
        if (!currentUserId) return
        const nextShared = !isClassImageShared(piece)
        const update = buildArtworkSharedUpdate(
          { [piece.id]: piece },
          piece.id,
          nextShared,
          currentUserId,
          nextShared ? new Date().toISOString() : undefined
        )
        if (!update) return
        try {
          await Firebase.getInstance().updateDocumentOrThrow(
            DOC_TYPES.STUDENTS,
            studentId,
            update
          )
          if (nextShared) {
            delivery.reset(studentId, piece.classSessionId)
          } else {
            delivery.unshared(studentId, piece.classSessionId, {
              kind: "image",
              studentId,
              artworkId: piece.id,
            })
          }
          void message.success(
            itemsSharedMessage(1, nextShared ? "shared" : "unshared")
          )
        } catch (err) {
          console.error("[standout] photo share failed", err)
          void message.error("Could not update this photo. Try again.")
        }
      }),
    [currentUserId, delivery, message, runPiece, studentId]
  )

  const writeDontShare = useCallback(
    (piece: StudentArtwork, nextDontShare: boolean, reason?: string) =>
      runPiece(dontShareKey(piece.id), async () => {
        const update = buildArtworkDontShareUpdate(
          { [piece.id]: piece },
          piece.id,
          nextDontShare,
          reason
        )
        if (!update) return
        try {
          await Firebase.getInstance().updateDocumentOrThrow(
            DOC_TYPES.STUDENTS,
            studentId,
            update
          )
          if (nextDontShare) {
            delivery.unshared(studentId, piece.classSessionId, {
              kind: "image",
              studentId,
              artworkId: piece.id,
            })
          }
          setReasonFor(null)
          void message.success(
            nextDontShare
              ? "Photo will not be sent to parents."
              : "Photo can be shared again."
          )
        } catch (err) {
          console.error("[standout] photo dont-share failed", err)
          void message.error("Could not update this photo. Try again.")
        }
      }),
    [delivery, message, runPiece, studentId]
  )

  if (allArtwork.length === 0) return null

  return (
    <div
      data-testid="sr-artwork-strip"
      data-expanded={expanded ? "true" : "false"}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 6,
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          overflowX: "auto",
          overflowY: "hidden",
          scrollbarWidth: "thin",
          borderRadius: 6,
          maxWidth: "100%",
        }}
      >
        {visible.map((piece) => (
          <ArtworkEditableThumb
            key={piece.id}
            studentId={studentId}
            artworkId={piece.id}
            artwork={piece}
            source={ARTWORK_EDIT_SOURCE.STANDOUT_REVIEW}
            size={TILE_SIZE}
            alt="Student artwork"
            viewLabel="View student artwork"
            testId="sr-artwork-thumb"
            showTileActions={true}
            onShare={canShare ? () => void sharePiece(piece) : undefined}
            sharePending={pendingKeys.has(shareKey(piece.id))}
            onDontShare={
              canShare
                ? () => {
                    if (isArtworkDontShare(piece)) {
                      void writeDontShare(piece, false)
                      return
                    }
                    setReasonFor(piece)
                  }
                : undefined
            }
            dontSharePending={pendingKeys.has(dontShareKey(piece.id))}
          />
        ))}
      </div>
      <DontShareReasonModal
        open={reasonFor !== null}
        subject="photo"
        pending={!!reasonFor && pendingKeys.has(dontShareKey(reasonFor.id))}
        onConfirm={(reason) => {
          if (reasonFor) void writeDontShare(reasonFor, true, reason)
        }}
        onCancel={() => setReasonFor(null)}
      />
      {hiddenCount > 0 ? (
        <Button
          size="small"
          data-testid="sr-artwork-show-more"
          onClick={toggleExpanded}
          style={{ width: "fit-content" }}
        >
          {expanded ? "See less" : "See more"}
        </Button>
      ) : null}
    </div>
  )
}

export default MomentArtworkStrip
